import { useEffect, useState } from 'react'
import {
  api,
  ErrorDeApi,
  type ConciliacionDelPeriodo as Conciliacion,
  type CuentaBancaria,
  type PartidaEnConciliacion,
} from '../servicios/api'
import { fechaContable } from '../fechas'

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]

const SIN_PERMISO = 'Tu rol no puede hacer este cambio: cerrar es del administrador o del contador, y reabrir solo del administrador.'

const mensaje = (e: unknown, respaldo: string) =>
  e instanceof ErrorDeApi ? (e.estado === 403 ? SIN_PERMISO : e.message) : respaldo

const pesos = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Con signo: positivo entró al banco, negativo salió. */
const conSigno = (n: number) => (n < 0 ? `−${pesos(-n)}` : pesos(n))

const instante = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })

/** Por omisión, el mes anterior al actual: el último que puede estar terminado. */
export function mesAnterior(hoy: Date): { anio: number; mes: number } {
  return hoy.getMonth() === 0 ? { anio: hoy.getFullYear() - 1, mes: 12 } : { anio: hoy.getFullYear(), mes: hoy.getMonth() }
}

function Partidas({ titulo, partidas, vacio }: { titulo: string; partidas: PartidaEnConciliacion[]; vacio: string }) {
  return (
    <>
      <h4>{titulo}</h4>
      {partidas.length === 0 ? (
        <p className="vacio">{vacio}</p>
      ) : (
        <div className="tabla-envoltura">
          <table className="tabla" aria-label={titulo}>
            <thead>
              <tr>
                <th scope="col">Fecha</th>
                <th scope="col">Concepto</th>
                <th scope="col" className="num">Importe</th>
                <th scope="col" className="num">Días</th>
              </tr>
            </thead>
            <tbody>
              {partidas.map((p) => (
                <tr key={`${p.lado}-${p.id}`}>
                  <td>{fechaContable(p.fecha)}</td>
                  <td>
                    {p.tipoPoliza && p.folio !== null && <>Póliza de {p.tipoPoliza} {p.folio} · </>}
                    {p.concepto}
                    {p.referencia && <div className="sutil mono" style={{ fontSize: 11 }}>ref. {p.referencia}</div>}
                    {p.nota && <div className="sutil" style={{ fontSize: 11 }}>{p.nota}</div>}
                  </td>
                  <td className="num">{conSigno(p.importe)}</td>
                  <td className="num">{p.dias}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/**
 * La conciliación bancaria de un mes como documento (RF-1308): el saldo según el estado de cuenta,
 * las partidas que explican la diferencia y el saldo contable. Mientras está abierta se recalcula;
 * cerrada es la foto del cierre y no cambia (RN-38). No se cierra con diferencia sin explicar
 * (RN-39): el servidor dice si se puede y, si no, por qué, y la pantalla lo muestra tal cual.
 *
 * Cerrar es del administrador y del contador; reabrir, solo del administrador y con motivo. Las
 * guardas de aquí son presentación: el servidor vuelve a revisar cada cambio.
 */
export function ConciliacionDelPeriodo({
  clienteId,
  cuenta,
  puedeCerrar,
  puedeReabrir,
  alCambiar,
  hoy = new Date(),
}: {
  clienteId: string
  cuenta: CuentaBancaria
  puedeCerrar: boolean
  puedeReabrir: boolean
  /** Tras cerrar o reabrir: el emparejamiento de ese mes queda bloqueado o desbloqueado. */
  alCambiar: () => void
  hoy?: Date
}) {
  const [inicial] = useState(() => mesAnterior(hoy))
  // Los últimos seis ejercicios: lo que un despacho concilia y lo que la ley pide conservar (RN-09).
  const [anios] = useState(() => Array.from({ length: 6 }, (_, i) => hoy.getFullYear() - i))
  const [anio, setAnio] = useState(inicial.anio)
  const [mes, setMes] = useState(inicial.mes)
  const [conciliacion, setConciliacion] = useState<Conciliacion>()
  const [error, setError] = useState('')
  const [aviso, setAviso] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [reabriendo, setReabriendo] = useState(false)
  const [motivo, setMotivo] = useState('')

  async function cargar(a = anio, m = mes) {
    setConciliacion(undefined)
    setError('')
    try {
      setConciliacion(await api.conciliacionBancaria(clienteId, cuenta.id, a, m))
    } catch (e) {
      setError(mensaje(e, 'No se pudo cargar la conciliación bancaria del periodo.'))
    }
  }

  useEffect(() => {
    void cargar()
  }, [clienteId, cuenta.id])

  function elegirPeriodo(a: number, m: number) {
    setAnio(a)
    setMes(m)
    setAviso('')
    setReabriendo(false)
    void cargar(a, m)
  }

  async function cerrar() {
    if (!window.confirm(`¿Cerrar la conciliación bancaria de ${MESES[mes - 1]} ${anio}? Cerrada no cambia: no se podrá emparejar, deshacer ni importar movimientos de ese mes hasta que un administrador la reabra.`)) return
    setOcupado(true)
    setError('')
    setAviso('')
    try {
      setConciliacion(await api.cerrarConciliacionBancaria(clienteId, cuenta.id, anio, mes))
      setAviso(`Cerraste la conciliación bancaria de ${MESES[mes - 1]} ${anio}.`)
      alCambiar()
    } catch (e) {
      setError(mensaje(e, 'No se pudo cerrar la conciliación bancaria.'))
    } finally {
      setOcupado(false)
    }
  }

  async function reabrir(evento: React.FormEvent) {
    evento.preventDefault()
    if (!motivo.trim()) {
      setError('Escribe por qué reabres la conciliación: queda en la bitácora del despacho.')
      return
    }
    setOcupado(true)
    setError('')
    setAviso('')
    try {
      setConciliacion(await api.reabrirConciliacionBancaria(clienteId, cuenta.id, anio, mes, motivo.trim()))
      setAviso(`Reabriste la conciliación bancaria de ${MESES[mes - 1]} ${anio}. El motivo quedó en la bitácora.`)
      setReabriendo(false)
      setMotivo('')
      alCambiar()
    } catch (e) {
      setError(mensaje(e, 'No se pudo reabrir la conciliación bancaria.'))
    } finally {
      setOcupado(false)
    }
  }

  async function exportar(formato: 'excel' | 'pdf') {
    setError('')
    try {
      await api.exportarConciliacionBancaria(clienteId, cuenta.id, anio, mes, formato)
    } catch (e) {
      setError(mensaje(e, 'No se pudo exportar la conciliación bancaria.'))
    }
  }

  const c = conciliacion
  const cerrada = c?.estado === 'Cerrada'

  return (
    <section aria-labelledby="titulo-conciliacion-del-periodo">
      <h2 id="titulo-conciliacion-del-periodo">Conciliación bancaria del periodo</h2>

      <div className="filtros" role="group" aria-label="Periodo de la conciliación bancaria">
        <label>
          Mes
          <select value={mes} onChange={(e) => elegirPeriodo(anio, Number(e.target.value))}>
            {MESES.map((nombre, i) => <option key={nombre} value={i + 1}>{nombre}</option>)}
          </select>
        </label>
        <label>
          Año
          <select value={anio} onChange={(e) => elegirPeriodo(Number(e.target.value), mes)}>
            {anios.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </label>
      </div>

      {error && <p className="error" role="alert">{error}</p>}
      {aviso && <p className="aviso" role="status">{aviso}</p>}
      {!c && !error && <p className="sutil">Cargando…</p>}

      {c && (
        <>
          <p>
            <span className={`insignia ${cerrada ? 'vigente' : 'aviso'}`}>{cerrada ? 'Cerrada' : 'Abierta'}</span>{' '}
            {cerrada && c.cerradaEnUtc && <span className="sutil">desde {instante(c.cerradaEnUtc)}: es la foto del cierre y no cambia.</span>}
            {!cerrada && c.reabiertaEnUtc && (
              <span className="sutil">
                Reabierta el {instante(c.reabiertaEnUtc)}{c.motivoDeReapertura ? ` · motivo: ${c.motivoDeReapertura}` : ''}.
              </span>
            )}
            {!cerrada && !c.reabiertaEnUtc && <span className="sutil">Se recalcula con cada emparejamiento hasta que la cierres.</span>}
          </p>

          {c.avisos.map((a) => <p key={a} className="aviso" role="status">{a}</p>)}

          <div className="tabla-envoltura">
            <table className="tabla" aria-label={`Conciliación bancaria de ${MESES[c.mes - 1]} ${c.anio}`}>
              <tbody>
                <tr>
                  <th scope="row">Saldo según el estado de cuenta al {fechaContable(c.fechaDeCorte)}</th>
                  <td className="num">{conSigno(c.saldoSegunBanco)}</td>
                </tr>
                <tr>
                  <th scope="row">
                    Menos: partidas del banco sin reflejo en la contabilidad ({c.partidasBancarias.length})
                  </th>
                  <td className="num">{conSigno(c.sumaPartidasBancarias)}</td>
                </tr>
                <tr>
                  <th scope="row">
                    Más: partidas de la contabilidad sin reflejo en el banco ({c.partidasContables.length})
                  </th>
                  <td className="num">{conSigno(c.sumaPartidasContables)}</td>
                </tr>
                {c.diferenciaInicial !== 0 && (
                  <tr>
                    <th scope="row">
                      Menos: diferencia inicial (el saldo inicial del banco contra la contabilidad a esa fecha)
                    </th>
                    <td className="num">{conSigno(c.diferenciaInicial)}</td>
                  </tr>
                )}
                <tr>
                  <th scope="row">Saldo contable de {cuenta.cuentaContable}</th>
                  <td className="num"><strong>{conSigno(c.saldoContable)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <p className="sutil">
            Diferencia entre el banco y la contabilidad: {conSigno(c.diferencia)}.{' '}
            {c.diferencia === 0
              ? 'Coinciden: no hay nada que explicar.'
              : c.sinExplicar === 0
                ? 'La explican por completo las partidas en conciliación de abajo.'
                : ''}
            {c.diferenciaInicial !== 0 && ` De ella, ${conSigno(c.diferenciaInicial)} viene del saldo inicial y ninguna partida la explica.`}
          </p>
          {c.sinExplicar !== 0 && (
            <p className="error" role="status">
              Sin explicar: {conSigno(c.sinExplicar)}. Ninguna partida identificada cubre esa parte de la diferencia (RN-39).
            </p>
          )}

          <Partidas titulo="Partidas del banco sin reflejo en la contabilidad" partidas={c.partidasBancarias}
            vacio="No hay partidas del banco sin reflejo en la contabilidad: todo lo del estado de cuenta del periodo está emparejado con pólizas." />
          <Partidas titulo="Partidas de la contabilidad sin reflejo en el banco" partidas={c.partidasContables}
            vacio={`No hay partidas de la contabilidad sin reflejo en el banco: todo lo registrado en ${cuenta.cuentaContable} en el periodo está emparejado.`} />

          <div className="acciones">
            <button type="button" className="secundario" onClick={() => void exportar('excel')}>Exportar Excel</button>
            <button type="button" className="secundario" onClick={() => void exportar('pdf')}>Exportar PDF</button>
            {puedeCerrar && !cerrada && (
              <>
                <button type="button" className="primario" disabled={!c.puedeCerrar || ocupado} onClick={() => void cerrar()}>
                  Cerrar la conciliación
                </button>
                {!c.puedeCerrar && c.motivoParaNoCerrar && <span className="sutil">{c.motivoParaNoCerrar}</span>}
              </>
            )}
            {puedeReabrir && cerrada && !reabriendo && (
              <button type="button" className="secundario" disabled={ocupado} onClick={() => { setReabriendo(true); setError('') }}>
                Reabrir
              </button>
            )}
          </div>

          {reabriendo && (
            <form onSubmit={reabrir} aria-label="Reabrir la conciliación bancaria">
              <label>
                Motivo de la reapertura (queda en la bitácora)
                <textarea value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} />
              </label>
              <div className="acciones">
                <button type="button" onClick={() => { setReabriendo(false); setMotivo('') }}>Cancelar</button>
                <button type="submit" className="primario" disabled={ocupado}>Reabrir la conciliación</button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  )
}
